import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  CircularProgress,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import dayjs, { Dayjs } from 'dayjs';
import { useGetCustomerReport } from 'src/services/report.service';
import { generateCustomerReportCSV } from 'src/utils/csv.utils';
import { generateCustomerReportPDF } from 'src/components/Invoice/CustomerReportPDF';
import MainLayout from 'src/pages/private';
import {
  ReportPageWrapper,
  FilterPaper,
  FilterActionsBox,
  FlexSpacer,
  GenerateButton,
  PdfIconButton,
  CsvIconButton,
  LoadingBox,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValue,
  StatValueError,
  StyledTable,
  StyledTableContainer,
  BorderlessRow,
  HeaderCell,
  DataCell,
  DataCellConditionalError,
  EmptyCell,
} from './ReportPage.styled';

const CustomerReport: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, reportData, fetchCustomerReport } =
    useGetCustomerReport();

  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf('month')
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const handleGenerateReport = useCallback(() => {
    fetchCustomerReport({
      startDate: startDate
        ? startDate.format('YYYY-MM-DD')
        : dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: endDate
        ? endDate.format('YYYY-MM-DD')
        : dayjs().endOf('month').format('YYYY-MM-DD'),
    });
  }, [startDate, endDate, fetchCustomerReport]);

  // Run once on mount — fetchCustomerReport is stable (useCallback with [])
  useEffect(() => {
    fetchCustomerReport({
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    });
  }, []);

  const handleExportCSV = () => {
    if (reportData) {
      generateCustomerReportCSV(reportData, {
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      });
    }
  };

  const handleExportPDF = async () => {
    if (reportData) {
      try {
        await generateCustomerReportPDF(reportData, {
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
        });
      } catch (err) {
        console.error('Error generating PDF:', err);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <MainLayout title="Customer Report">
      <ReportPageWrapper>
        <FilterPaper elevation={0}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={6}>
                <FilterActionsBox>
                  <GenerateButton
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleGenerateReport}
                    disabled={loading}
                  >
                    Generate
                  </GenerateButton>
                  <FlexSpacer />
                  <Tooltip title="Export to PDF">
                    <span>
                      <PdfIconButton
                        onClick={handleExportPDF}
                        disabled={loading || !reportData}
                      >
                        <PictureAsPdfIcon fontSize="small" />
                      </PdfIconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Export to CSV">
                    <span>
                      <CsvIconButton
                        onClick={handleExportCSV}
                        disabled={loading || !reportData}
                      >
                        <DescriptionIcon fontSize="small" />
                      </CsvIconButton>
                    </span>
                  </Tooltip>
                </FilterActionsBox>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </FilterPaper>

        {loading ? (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        ) : (
          <>
            <SummaryGrid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    Total Orders
                  </StatLabel>
                  <StatValue variant="h4">
                    {(reportData?.summary.totalOrders || 0).toLocaleString()}
                  </StatValue>
                </StatCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    Total Revenue
                  </StatLabel>
                  <StatValue variant="h4">
                    {formatCurrency(reportData?.summary.totalRevenue || 0)}
                  </StatValue>
                </StatCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    Total Balance
                  </StatLabel>
                  <StatValueError variant="h4">
                    {formatCurrency(reportData?.summary.totalBalance || 0)}
                  </StatValueError>
                </StatCard>
              </Grid>
            </SummaryGrid>

            <StyledTableContainer>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <HeaderCell>Customer Name</HeaderCell>
                    <HeaderCell align="center">No. of Orders</HeaderCell>
                    <HeaderCell align="right">Grand Total</HeaderCell>
                    <HeaderCell align="right">Paid Amount</HeaderCell>
                    <HeaderCell align="right">Balance Amount</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData && reportData.customers.length > 0 ? (
                    reportData.customers.map((row) => (
                      <BorderlessRow key={row.customerId}>
                        <DataCell component="th" scope="row">
                          {row.customerName}
                        </DataCell>
                        <DataCell align="center">{row.noOfOrders}</DataCell>
                        <DataCell align="right">
                          {formatCurrency(row.grandTotal)}
                        </DataCell>
                        <DataCell align="right">
                          {formatCurrency(row.paidAmount)}
                        </DataCell>
                        <DataCellConditionalError
                          align="right"
                          highlight={row.balanceAmount > 0}
                        >
                          {formatCurrency(row.balanceAmount)}
                        </DataCellConditionalError>
                      </BorderlessRow>
                    ))
                  ) : (
                    <TableRow>
                      <EmptyCell colSpan={5} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No customer order data found for the selected date
                          range.
                        </Typography>
                      </EmptyCell>
                    </TableRow>
                  )}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </>
        )}
      </ReportPageWrapper>
    </MainLayout>
  );
};

export default CustomerReport;
